import React, { useState, useCallback, useMemo } from 'react';
import { Modal, Stack, Text, MessageBar, MessageBarType } from '@fluentui/react';
import { useUiContext } from '../../../../components/Contexts/UIContext/uiCtx';
import { createSuggestionProps } from '../SuggestionProps';
import { CreateTimeEntryCorrectionModalProps } from './types';
import { useTaskSearch } from './hooks/useTaskSearch';
import { useUserSearch } from './hooks/useUserSearch';
import { useAssignmentPreview } from './hooks/useAssignmentPreview';
import { useFormValidation } from './hooks/useFormValidation';
import { useCreateCorrection } from './hooks/useCreateCorrection';
import { TaskPicker } from './components/TaskPicker';
import { UserPicker } from './components/UserPicker';
import { AssignmentInfoDisplay } from './components/AssignmentInfoDisplay';
import { RateSelectionSection } from './components/RateSelectionSection';
import { BasicFormFields } from './components/BasicFormFields';
import { ModalActions } from './components/ModalActions';

export const CreateTimeEntryCorrectionModal: React.FC<CreateTimeEntryCorrectionModalProps> = ({ isOpen, onDismiss, onSuccess }) => {
    const [date, setDate] = useState<Date | null>(null);
    const [correctionHours, setCorrectionHours] = useState<string>('');
    const [comment, setComment] = useState<string>('');

    const { isDarkmode } = useUiContext();

    // Custom hooks for business logic
    const taskSearch = useTaskSearch();
    const userSearch = useUserSearch();
    const assignmentPreview = useAssignmentPreview({
        selectedUser: userSearch.selectedUser,
        selectedTask: taskSearch.selectedTask,
        date,
        isOpen,
    });

    const validation = useFormValidation({
        selectedUser: userSearch.selectedUser,
        selectedTask: taskSearch.selectedTask,
        date,
        correctionHours,
        comment,
        assignmentInfo: assignmentPreview.assignmentInfo,
        selectedActivity: assignmentPreview.selectedActivity,
        customRate: assignmentPreview.customRate,
    });

    const handleClose = useCallback(() => {
        taskSearch.setSelectedTask(null);
        userSearch.setSelectedUser(null);
        setDate(null);
        setCorrectionHours('');
        setComment('');
        assignmentPreview.setSelectedActivity(null);
        assignmentPreview.setCustomRate('');
        onDismiss();
    }, [onDismiss, taskSearch, userSearch, assignmentPreview]);

    const correction = useCreateCorrection({
        onSuccess,
        onClose: handleClose,
    });

    const handleSubmit = useCallback(async () => {
        const result = validation.validateAndGetData();

        if (!result.isValid) {
            correction.setError(result.error || 'Please fill in all required fields');
            return;
        }

        if (result.data) {
            correction.createCorrection(result.data);
        }
    }, [validation, correction]);

    // Suggestion props
    const suggestionProps = useMemo(() => createSuggestionProps({ isDarkmode }), [isDarkmode]);

    return (
        <Modal isOpen={isOpen} onDismiss={handleClose} isBlocking={false} containerClassName="create-correction-modal">
            <div style={{ padding: '20px', minWidth: '450px', maxWidth: '600px' }}>
                <Stack tokens={{ childrenGap: 20 }}>
                    <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                        Create Time Entry Correction
                    </Text>

                    <Text variant="small" styles={{ root: { color: isDarkmode ? '#C8C6C4' : '#605E5C' } }}>
                        This will create a correction without requiring an existing time entry. A time entry with 0 hours will be created automatically with an
                        approved state, and the correction will be immediately visible.
                    </Text>

                    {correction.error && (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => correction.setError('')}>
                            {correction.error}
                        </MessageBar>
                    )}

                    {/* Task Selection */}
                    <TaskPicker
                        onTaskResolveSuggestions={taskSearch.onTaskResolveSuggestions}
                        onTaskSelected={taskSearch.onTaskSelected}
                        onRemoveTask={taskSearch.onRemoveTask}
                        selectedTaskPersona={taskSearch.selectedTaskPersona}
                        suggestionProps={suggestionProps}
                        isDarkmode={isDarkmode}
                    />

                    {/* User Selection */}
                    <UserPicker
                        onUserResolveSuggestions={userSearch.onUserResolveSuggestions}
                        onUserSelected={userSearch.onUserSelected}
                        onRemoveUser={userSearch.onRemoveUser}
                        selectedUserPersona={userSearch.selectedUserPersona}
                        suggestionProps={suggestionProps}
                    />

                    {/* Assignment Information Display */}
                    <AssignmentInfoDisplay
                        assignmentInfo={assignmentPreview.assignmentInfo}
                        isLoadingPreview={assignmentPreview.isLoadingPreview}
                        selectedUser={userSearch.selectedUser}
                        selectedTask={taskSearch.selectedTask}
                    />

                    {/* Rate Selection Section */}
                    <RateSelectionSection
                        selectedUser={userSearch.selectedUser}
                        selectedTask={taskSearch.selectedTask}
                        assignmentInfo={assignmentPreview.assignmentInfo}
                        isLoadingPreview={assignmentPreview.isLoadingPreview}
                        activities={assignmentPreview.activities}
                        selectedActivity={assignmentPreview.selectedActivity}
                        setSelectedActivity={assignmentPreview.setSelectedActivity}
                        customRate={assignmentPreview.customRate}
                        setCustomRate={assignmentPreview.setCustomRate}
                    />

                    {/* Basic Form Fields */}
                    <BasicFormFields
                        date={date}
                        setDate={setDate}
                        correctionHours={correctionHours}
                        setCorrectionHours={setCorrectionHours}
                        comment={comment}
                        setComment={setComment}
                    />

                    {/* Action Buttons */}
                    <ModalActions onCancel={handleClose} onSubmit={handleSubmit} isSubmitting={correction.isPending} isValid={validation.isValid} />
                </Stack>
            </div>
        </Modal>
    );
};
